/**
 * Contraste dos dados sobre a foto de fundo.
 *
 * Os símbolos e linhas das camadas foram desenhados para o fundo escuro do
 * GOES. Do zoom máximo dele em diante o fundo passa a ser a foto de satélite do fundo —
 * clara, cheia de detalhe — e o que era legível some: a gota vazada do
 * pluviômetro sem chuva (contorno azul-claro de 1 px) desaparece no meio das
 * casas, o marco da sondagem e o círculo do nível do rio se confundem com o
 * chão, o contorno de 0,6 px dos municípios some nas ruas.
 *
 * O mesmo vale nos zooms afastados quando a foto de satélite do fundo entra ao fundo nos
 * quadros sem a imagem da NOAA (fundoSemNoaa.ts, `definirFotoAoFundo`).
 *
 * Ali, e só ali (sobre o GOES nada muda):
 * - cada PONTO ganha um selo — um círculo escuro translúcido com borda clara
 *   fina: o fundo para o qual o símbolo foi pensado, só em volta dele, que
 *   também o separa da foto;
 * - cada LINHA fina sem casco ganha um casco escuro por baixo (o mesmo das
 *   divisas de estado e das rodovias).
 */
import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import { FUNDO_MAX_RESOLUTION } from './fundoLayer';

/** A foto de satélite do fundo está ao fundo em todos os zooms (quadro sem a imagem da NOAA)? */
let fotoAoFundo = false;

/** Liga ou desliga a foto ao fundo em todos os zooms; true se mudou (as camadas devem se redesenhar). */
export function definirFotoAoFundo(on: boolean): boolean {
  if (fotoAoFundo === on) return false;
  fotoAoFundo = on;
  return true;
}

/** O fundo nesta resolução é (ou está virando) a foto de satélite do fundo? */
export function sobreImagem(resolution: number | undefined): boolean {
  return resolution !== undefined && (fotoAoFundo || resolution < FUNDO_MAX_RESOLUTION);
}

/** Fundo do selo: a tinta escura do app, translúcida. */
export const COR_SELO = 'rgba(11, 18, 32, 0.78)';
export const BORDA_SELO = 'rgba(255, 255, 255, 0.72)';
/** Casco das linhas (o mesmo de `casedLineStyles` nos vetores). */
export const COR_CASCO = 'rgba(6, 10, 18, 0.55)';

/** Abaixo de tudo o que a camada desenha (os estilos dos símbolos usam zIndex ≥ −1). */
const Z_SELO = -10;

const selos = new Map<string, Style>();

/**
 * Selo de raio `raio` px, deslocado `subir` px para cima (o marco da sondagem
 * sai da base e sobe: o selo fica no meio dele). Em cache por tamanho.
 */
export function seloDeContraste(raio: number, subir = 0): Style {
  const r = Math.round(raio * 2) / 2;
  const d = Math.round(subir * 2) / 2;
  const k = `${r}|${d}`;
  let s = selos.get(k);
  if (!s) {
    s = new Style({
      image: new CircleStyle({
        radius: r,
        fill: new Fill({ color: COR_SELO }),
        stroke: new Stroke({ color: BORDA_SELO, width: 1 }),
        displacement: [0, d],
      }),
      zIndex: Z_SELO,
    });
    selos.set(k, s);
  }
  return s;
}

/** Os estilos do símbolo, com o selo por baixo quando o fundo é a foto. */
export function comSelo(estilos: Style[], raio: number, resolution: number | undefined, subir = 0): Style[] {
  return estilos.length && sobreImagem(resolution) ? [seloDeContraste(raio, subir), ...estilos] : estilos;
}

const cascos = new Map<string, Style>();

/** Casco escuro de uma linha de largura `largura` (2 px mais largo). */
export function cascoDeLinha(largura: number): Style {
  const k = largura.toFixed(2);
  let s = cascos.get(k);
  if (!s) {
    s = new Style({
      stroke: new Stroke({ color: COR_CASCO, width: largura + 2, lineJoin: 'round', lineCap: 'round' }),
      zIndex: -1,
    });
    cascos.set(k, s);
  }
  return s;
}
